<template>
  <div
    ref="$el"
    class="related-post"
    @click.stop="onClick"
  >
    <UiComponentImage
      v-if="post.coverImage && post.coverImage.url"
      :key="`post-${elementWidth}x${elementHeight}`"
      :alt="post.title"
      :expected-size="elementWidth"
      :extra-params="{
        h: elementHeight,
        w: elementWidth,
        fit: 'crop',
      }"
      :height="post.coverImage.height"
      :source="post.coverImage.url"
      use-natural-height
      use-natural-width
      :width="post.coverImage.width"
    />
    <UiComponentTag type="secondary">
      {{ post.category }}
    </UiComponentTag>
    <AtomAnchor :to="post.slug">
      <UiComponentHeading
        as="paragraph"
        :level="4"
      >
        {{ post.title }}
      </UiComponentHeading>
    </AtomAnchor>
    <UiComponentParagraph
      v-if="computeReadingTime"
      class="description"
      size="s"
    >
      <span class="date">{{ formatDate(post.date) }} • </span>
      <span>{{ readingTime }}</span>
    </UiComponentParagraph>
  </div>
</template>

<script setup>
import dayjs from 'dayjs'

// Composables
const { width: screenWidth } = useScreenSize()

// Props
const props = defineProps({
  computeReadingTime: {
    default() {
      return true
    },
    required: false,
    type: Boolean,
  },
  post: {
    required: true,
    type: Object,
  },
})

// Refs
const $el = ref(null)
const elementWidth = ref(400)
const resizeObserver = ref(null)

// Computed Properties
const elementHeight = computed(() => {
  return elementWidth.value / ratio.value
})

const ratio = computed(() => {
  return screenWidth.value >= 768
    ? 292 / 212
    : 279 / 181
})

const readingTime = computed(() => {
  if (props.post.readingTime) {
    return props.post.readingTime
  }
  else if (props.post.content) {
    const wordsPerMinute = 1500
    const allContentLength = props.post?.content.reduce(
      (accumulator, item) => {
        if (item?.__typename !== 'TextRecord') {
          return accumulator
        }
        return accumulator + item.text.length
      },
      0,
    )
    const minutes = Math.floor(allContentLength / wordsPerMinute)
    return minutes > 0 ? `About ${minutes} min. read` : '1 min. read'
  }
  else {
    return undefined
  }
})

// Methods
function formatDate(date) {
  return dayjs(date).format('MMMM D, YYYY')
}

function onClick() {
  $el.value.querySelector('.anchor').click()
}

function onObserve() {
  elementWidth.value = $el.value.offsetWidth
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  resizeObserver.value.unobserve($el.value)
})

onMounted(() => {
  resizeObserver.value = new ResizeObserver(onObserve)
  resizeObserver.value.observe($el.value)
})
</script>

<style lang="postcss" scoped>
.related-post {
  @apply flex flex-col w-full items-center cursor-pointer;

  picture {
    @apply bg-grey-blue-200 rounded-[24px] overflow-hidden w-full;
  }

  .tag {
    @apply rounded-xs transform -translate-y-1/2;
  }

  a {
    @apply flex justify-center mt-4;

    .h4 {
      @apply font-medium text-center text-winter-green-900 text-xl leading-7 !important;

      @screen mdminus {
        @apply text-2xl leading-8 !important;
      }

      @screen mdplus {
        @apply text-2xl leading-8 !important;
      }
    }
  }

  .description {
    @apply flex items-center mr-1 text-grey-blue-600 text-center normal-case mt-2 !important;
  }
}
</style>
